import { Environment } from './Logger';
import { pathJoin } from './stringUtils';

// Minimal shape needed from IDatabase to avoid circular imports
export interface IHasDatabaseUrl {
  name: string;
  testkundURL?: string | null;
}

export interface IRedirectOptions {
  performRedirect?: boolean; // default true in router, false when only computing state
  pathname?: string; // allow explicit pathname injection for testability
}

export interface IRedirectResult {
  redirecting: boolean; // true if a redirect was (or would be) initiated
  databaseUrl?: string;
  canRedirect: boolean;
}

/**
 * Shared logic for determining (and optionally performing) redirect to selected database URL.
 * Extracted from BaseRouter and reused in LoginStateContext to keep a single source of truth.
 */
export const maybeRedirectToDatabase = (
  databases: IHasDatabaseUrl[] | undefined,
  activeDatabaseName: string | undefined,
  options: IRedirectOptions = {}
): IRedirectResult => {
  const { performRedirect = true } = options;
  const currentLocation = window.location.href;
  const pathname = options.pathname ?? window.location.pathname;
  const databaseUrl =
    databases?.find(db => db.name === activeDatabaseName)?.testkundURL ||
    undefined;

  const canRedirect =
    typeof window.env.canRedirect === 'boolean'
      ? window.env.canRedirect
      : typeof window.env.canRedirect === 'string' &&
        !!databaseUrl &&
        databaseUrl.startsWith(window.env.canRedirect)
      ? true
      : window.env.environment === Environment.production;

  if (databaseUrl && !currentLocation.startsWith(databaseUrl)) {
    if (canRedirect) {
      if (performRedirect) {
        window.location.href = pathJoin([databaseUrl, '#', pathname]);
      }
      return { redirecting: true, databaseUrl, canRedirect };
    } else {
      // eslint-disable-next-line no-console
      console.log(`Database url is ${databaseUrl} but canRedirect is false.`);
    }
  }
  return { redirecting: false, databaseUrl, canRedirect };
};

export default maybeRedirectToDatabase;
